import React, { useState, useEffect } from 'react';
import { Autocomplete, Button, TextField } from '@mui/material';
import axios from 'axios';
import { toast } from "react-toastify";
import CheckIcon from '@mui/icons-material/Check';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Box } from '@mui/system';

interface GoogleImageSearchProps {
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
   onUploadSuccess?: () => void;
}

const GoogleImageSearch: React.FC<GoogleImageSearchProps> = ({ searchQuery, setSearchQuery,onUploadSuccess }) => {
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const numResults = 10;
  const [selectedGoogleImage, setSelectedGoogleImage] = useState<string | null>(null);

  const [keywords, setKeywords] = useState('');
  const [keywordsArray, setKeywordsArray] = useState<string[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<any[]>([]);
  const [categoryError, setCategoryError] = useState(false);
  const [isConfidential, setIsConfidential] = useState(false);
  const [imageName, setImageName] = useState('');
  const [uploadedImageURL, setUploadedImageURL] = useState<string | null>(null);


const [previewImage, setPreviewImage] = useState<string | null>(null);
const [previewPosition, setPreviewPosition] = useState<{ x: number; y: number } | null>(null);
const [contextMenu, setContextMenu] = useState<{ x: number; y: number; imageUrl: string } | null>(null);
const [imageNameError, setImageNameError] = useState(false);

  const apiKey = 'AIzaSyCvpCtq77XelgCS6yD2vQv1JRE3YVymou0';
  const cx = '30dca0bba2d8b4b27';

  const fetchResults = async (page: number, shouldOpenModal = true) => {
  if (!searchQuery.trim()) return;
  setLoading(true);
  setImagesLoaded(0);

  try {
    const startIndex = (page - 1) * numResults + 1;
    const rightsFilter = 'cc_publicdomain|cc_attribute|cc_commercial|cc_nonderived';
    const apiUrl = `https://www.googleapis.com/customsearch/v1?q=${encodeURIComponent(
      searchQuery,
    )}&cx=${cx}&searchType=image&key=${apiKey}&num=${numResults}&start=${startIndex}&rights=${rightsFilter}`;

    const response = await fetch(apiUrl);
    const data = await response.json();

    const results = (data.items || []).filter((item: any) => {
      const link = item.link?.toLowerCase();
      return link && (link.endsWith('.jpg') || link.endsWith('.jpeg') || link.endsWith('.png'));
    });

    setSearchResults(results);
    if (shouldOpenModal) setIsModalOpen(true);
  } catch (error) {
    console.error('Error fetching search results:', error);
    setSearchResults([]);
  }
};


  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await axios.post(`/treatment_category`);
        setCategories(data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  const handleKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeywords(e.target.value);
  };

  const handleKeywordAdd = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && keywords.trim()) {
      e.preventDefault();
      const newKeyword = keywords.trim();
      if (!keywordsArray.includes(newKeyword)) {
        setKeywordsArray([...keywordsArray, newKeyword]);
      }
      setKeywords('');
    }
  };

  const handleRemoveKeyword = (keywordToRemove: string) => {
    setKeywordsArray(keywordsArray.filter((k) => k !== keywordToRemove));
  };

  useEffect(() => {
    if (hasSearched) {
      fetchResults(currentPage);
    }
  }, [currentPage]);

  useEffect(() => {
    if (searchResults.length > 0 && imagesLoaded === searchResults.length) {
      setLoading(false); // All images finished loading
    }
  }, [imagesLoaded, searchResults]);

  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    setCurrentPage(1);
    setHasSearched(true);
    fetchResults(1);
  };

  const handleClear = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsModalOpen(false);
    setCurrentPage(1);
    setHasSearched(false);
  };



  const handleNextPage = () => setCurrentPage((prevPage) => prevPage + 1);
  const handlePrevPage = () => setCurrentPage((prevPage) => (prevPage > 1 ? prevPage - 1 : 1));

  const handleGoogleImageSelect = (link: string) => {
    setSelectedGoogleImage(link);
  };

  const handleImageLoad = () => {
    setImagesLoaded((prev) => prev + 1);
  };

  const handleImageSearchUpload = async () => {
    console.log("Selected Google Image:", selectedGoogleImage);
    if (selectedCategories.length === 0) {
      setCategoryError(true);
      return;
    }
    setCategoryError(false);

    const token = axios.defaults.headers.common.Authorization;
    setLoading(true);
    const categoryIds = selectedCategories.map(cat => cat.id);

    if (!selectedGoogleImage) {
      toast.error("No image URL selected.");
      setLoading(false);
      return;
    }

    if (!imageName.trim()) {
      toast.error("Image name is required.");
      setImageNameError(true);
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();

      formData.append("image_url", selectedGoogleImage);
      formData.append("is_confidential", isConfidential ? "true" : "false");
      formData.append("user_file_name", imageName);
      formData.append("keywords", keywordsArray);
      categoryIds.forEach((id) => {
        formData.append("category_ids", id.toString());
      });

      

      await axios.post("consent_clause/upload_google_image_to_s3", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: token,
        },
      });

      toast.success("Content added to the Image gallery");

      
      setSelectedCategories([]);
      setKeywords('');
      setKeywordsArray([]);
      await fetchResults(1, false); 
      // handleClear()
      setIsModalOpen(false);
      closeModal();
      
  onUploadSuccess();

      
    } catch (error) {
      console.error("Error uploading image URL:", error);
      toast.error("Failed to upload image.");
    } finally {
      setLoading(false);
    }
  };

useEffect(() => {
  const handleCloseContext = () => setContextMenu(null);
  window.addEventListener('click', handleCloseContext);
  window.addEventListener('scroll', handleCloseContext);
  return () => {
    window.removeEventListener('click', handleCloseContext);
    window.removeEventListener('scroll', handleCloseContext);
  };
}, []);

 const closeModal = () => {
    setSearchQuery('');
    setSearchResults([]);
    setCurrentPage(1);
    setHasSearched(false);
    setImageName('');
    setKeywords('');
    setKeywordsArray([]);
    setSelectedGoogleImage(null);
    setSelectedCategories([]);
    setIsModalOpen(false);
  };


  return (
    
    <>
    {previewImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1001,
          }}
          onClick={() => setPreviewImage(null)}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '10px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
              maxWidth: '90%',
              maxHeight: '90%',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()} 
          >
            <button
              onClick={() => setPreviewImage(null)}
              style={{
                position: 'absolute',
                top: '10px',
                right: '15px',
                fontSize: '20px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              &times;
            </button>
            <img
              src={previewImage}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                borderRadius: '8px',
              }}
            />
          </div>
        </div>
      )}

      <button
        onClick={handleSearch}
        disabled={loading || !searchQuery.trim()}
        style={{
          padding: '14px 20px',
          borderRadius: '8px',
          marginLeft: '10px',
          backgroundColor: '#0056b3',
          color: 'white',
          border: 'none',
          fontSize: '18px',
          cursor: 'pointer',
          opacity: loading || !searchQuery.trim() ? 0.5 : 1,
        }}
      >
        {loading ? 'Loading...' : 'Search'}
      </button>

      {searchQuery && (
        <button
          onClick={handleClear}
          style={{
            padding: '14px 20px',
            borderRadius: '8px',
            marginLeft: '10px',
            backgroundColor: '#aaa',
            color: 'white',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
          }}
        >
          Clear
        </button>
      )}

      {isModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '30px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
              position: 'relative',
            }}
          >
            {/* Close Button */}
            <button
              
              onClick={closeModal}
              style={{
                position: 'absolute',
                top: '15px',
                right: '20px',
                fontSize: '24px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#444',
              }}
            >
              &times;
            </button>
                <p
                style={{
                    fontSize: '14px',
                    color: '#666',
                    marginTop: '-20px',
                    marginBottom: '20px',
                }}
                >
                💡 Right-click on an image to preview it in a new tab
                </p>
            <h2 style={{ marginBottom: '25px' }}>
              Search Results for "<strong>{searchQuery}</strong>" (Page {currentPage})
            </h2>
            {/* Search Input and Button */}
            <Box marginBottom={3} display="flex" justifyContent="center">
              <input
                  type="text"
                  placeholder="Search Google Images..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && {} }
                  style={{
                    width: '80%',
                    padding: '14px 40px 14px 14px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '18px',
                    backgroundColor: '#f9f9f9',
                  }}
                />

                <button
                  onClick={handleSearch}
                  disabled={loading || !searchQuery.trim()}
                  style={{
                    padding: '14px 20px',
                    borderRadius: '8px',
                    marginLeft: '10px',
                    backgroundColor: '#0056b3',
                    color: 'white',
                    border: 'none',
                    fontSize: '18px',
                    cursor: 'pointer',
                   
                  }}
                >
                  Search
                </button>

            {searchQuery && (
              <button
                onClick={handleClear}
                style={{
                  padding: '14px 20px',
                  borderRadius: '8px',
                  marginLeft: '10px',
                  backgroundColor: '#aaa',
                  color: 'white',
                  border: 'none',
                  fontSize: '18px',
                  cursor: 'pointer',
                }}
              >
                Clear
              </button>
            )}
       </Box>
            {/* Image Grid */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '15px',
                marginBottom: '30px',
              }}
            >
              {searchResults.length === 0 && !loading && <p>No results found.</p>}
              {searchResults.map((result, index) => (
            <div
                key={index}
                style={{ position: 'relative', width: '200px', height: '200px' }}
            >
                <img
                src={result.link}
                alt="Image result"
                onClick={() => handleGoogleImageSelect(result.link)}
                onLoad={handleImageLoad}
                onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenu({
                    x: e.pageX,
                    y: e.pageY,
                    imageUrl: result.link,
                    });
                }}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border:
                    selectedGoogleImage === result.link ? '4px solid #0056b3' : '2px solid #ddd',
                    boxShadow:
                    selectedGoogleImage === result.link ? '0 0 10px #0056b3' : 'none',
                    transition: 'all 0.3s ease',
                }}
                />

            {selectedGoogleImage === result.link && (
            <div
                style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: '#28a745', 
                borderRadius: '50%',
                width: '70px',     
                height: '70px',    
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '20px',
                fontWeight: 'bold',
                boxShadow: '0 0 12px rgb(7, 114, 255)', 
                }}
            >
                <CheckBoxIcon style={{ fontSize: 28 }} />
            </div>
            )}
            </div>
            ))}

            {contextMenu && (
            <div
                style={{
                position: 'fixed',
                top: contextMenu.y,
                left: contextMenu.x,
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                borderRadius: '6px',
                padding: '8px 0',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                zIndex: 9999,
                fontSize: '14px',
                width: '180px',
                }}
                onContextMenu={(e) => e.preventDefault()}
            >
                <div
                onClick={() => {
                    setPreviewImage(contextMenu.imageUrl);
                    setContextMenu(null);
                }}
                style={{
                    padding: '10px 16px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#fff')}
                >
                👁️ Preview
                </div>
                <div
                onClick={() => {
                    window.open(contextMenu.imageUrl, '_blank');
                    setContextMenu(null);
                }}
                style={{
                    padding: '10px 16px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#fff')}
                >
                🔗 Open in new tab
                </div>
            </div>
            )}
         </div>





            {/* Pagination */}
            {searchResults.length > 0 && (
              <div style={{ textAlign: 'center', marginBottom: '30px' }}>
               
               <Button
                onClick={handlePrevPage}
                style={{
                  padding: '8px 16px',
                  marginRight: '10px',
                  backgroundColor: '#f0f0f0',
                  color: '#333',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  transition: 'all 0.3s ease',
                }}
              >
                Previous
              </Button>

              <Button
                onClick={handleNextPage}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  transition: 'background-color 0.3s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#0056b3')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#007bff')}
              >
                Next
              </Button>


              </div>
            )}

            {/* Form Section */}
            <div
              style={{
                padding: '25px',
                backgroundColor: '#f4f6f8',
                borderRadius: '10px',
                marginTop: '30px',
              }}
            >
              {/* Confidential Toggle */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontWeight: 500 }}>
                  <input
                    type="checkbox"
                    checked={isConfidential}
                    onChange={(e) => setIsConfidential(e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Mark this image as <strong>confidential</strong>
                </label>
              </div>

              {/* Image Name Input */}
             <TextField
  fullWidth
  required
  label="Image Name"
  variant="outlined"
  placeholder="Enter image name..."
  value={imageName}
  onChange={(e) => {
    setImageName(e.target.value);
    if (e.target.value.trim()) setImageNameError(false); // clear error as user types
  }}
  error={imageNameError}
  helperText={imageNameError ? 'Image name is required' : ''}
  style={{ marginBottom: '15px', backgroundColor: '#fff' }}
/>


              {/* Category Selector */}
              <Autocomplete
                multiple
                id="categories-autocomplete"
                options={categories}
                getOptionLabel={(option) => option.category_name}
                value={selectedCategories}
                onChange={(event, newValue) => {
                  setSelectedCategories(newValue);
                  setCategoryError(false);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select categories"
                    placeholder="Start typing to search..."
                    error={categoryError}
                    helperText={categoryError ? 'Please select at least one category' : ''}
                    sx={{
                      marginBottom: '20px',
                      backgroundColor: '#f9f9f9',
                      borderRadius: '8px',
                    }}
                  />
                )}
                sx={{ width: '100%' }}
              />

              {/* Keyword Input */}
              <TextField
                fullWidth
                label="Enter keywords"
                variant="outlined"
                placeholder="Press Enter to add"
                value={keywords}
                onChange={handleKeywordChange}
                onKeyDown={handleKeywordAdd}
                style={{ marginBottom: '15px', backgroundColor: '#fff' }}
              />

              {/* Keyword Chips */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                {keywordsArray.map((keyword, index) => (
                  <span
                    key={index}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '20px',
                      padding: '6px 12px',
                      fontSize: '14px',
                    }}
                  >
                    {keyword}
                    <button
                      onClick={() => handleRemoveKeyword(keyword)}
                      style={{
                        marginLeft: '8px',
                        background: 'none',
                        border: 'none',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>

              <button
                onClick={handleImageSearchUpload}
                style={{
                  padding: '14px 24px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginTop: '10px',
                }}
                disabled={loading}
              >
                Upload Image
                
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GoogleImageSearch;
